import React from 'react';
import { format } from 'date-fns';
import { formatDueDate } from '../../utils/date-utils';
import { AreaIndicator, CustomFlagIndicator, WorkOrderBadge, WorkOrderUrgentBadge } from '../job-indicators';
import { OccupancyBadge, NextCheckInRow } from './WorkOrderOccupancyBadges';
import WorkOrderActiveTimerIndicator from './WorkOrderActiveTimerIndicator';
import { WorkOrderType, getWorkOrderTypeOption } from '../../utils/work-order-types';

const TYPE_BADGE_CLASSES = {
  [WorkOrderType.GUEST_IMPACT]: 'bg-blue-100 text-blue-800',
  [WorkOrderType.PROPERTY_CARE]: 'bg-amber-100 text-amber-800',
  [WorkOrderType.HOUSEKEEPING]: 'bg-emerald-100 text-emerald-800',
  [WorkOrderType.FEEDBACK]: 'bg-purple-100 text-purple-800',
};

const WorkOrderListItem = ({
  workOrder,
  index = 0,
  getStatusColor,
  reservationsByProperty,
  onViewWorkOrder,
  showAssignedBadge = false,
  showFeedbackBadge = false,
  showAssignmentDetails = false,
}) => {
  const handleView = () => {
    onViewWorkOrder?.(workOrder.id);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleView();
    }
  };

  return (
    <li
      role="button"
      tabIndex={0}
      onClick={handleView}
      onKeyDown={handleKeyDown}
      className={`p-4 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
        workOrder.isUrgent
          ? 'bg-red-50 hover:bg-red-100'
          : index % 2 === 0
            ? 'bg-white hover:bg-blue-50'
            : 'bg-gray-50 hover:bg-blue-50'
      }`}
      aria-label={`View details for ${workOrder.property?.name || 'work order'}`}
    >
      <div className="flex flex-col space-y-2">
        <WorkOrderActiveTimerIndicator hasActiveTimer={workOrder.hasActiveTimer} />
        <div className="flex justify-between items-start">
          <div className="text-sm font-medium text-gray-900">{workOrder.property?.name}</div>

          <div className="flex flex-wrap items-center justify-end gap-1">
            {workOrder.workOrderType && workOrder.workOrderType !== WorkOrderType.GUEST_IMPACT && (
              <WorkOrderBadge className={TYPE_BADGE_CLASSES[workOrder.workOrderType] || 'bg-gray-100 text-gray-800'}>
                {getWorkOrderTypeOption(workOrder.workOrderType).shortLabel}
              </WorkOrderBadge>
            )}
            {workOrder.customFlags?.map(flag => (
              <CustomFlagIndicator key={flag.id} flag={flag} />
            ))}
            {showFeedbackBadge && !workOrder.workOrderType && (
              <WorkOrderBadge className="bg-purple-100 text-purple-800">
                Feedback
              </WorkOrderBadge>
            )}
            {workOrder.isUrgent && (
              <WorkOrderUrgentBadge />
            )}
            {workOrder.streamlineWorkOrderId && (
              <WorkOrderBadge className="bg-yellow-100 text-yellow-800">
                Streamline
              </WorkOrderBadge>
            )}
            {workOrder.isGuestSubmitted && (
              <WorkOrderBadge className="bg-emerald-100 text-emerald-800">
                Guest
              </WorkOrderBadge>
            )}
            {workOrder.guestAlertsEnabled && (
              <WorkOrderBadge className="bg-teal-100 text-teal-800">
                Guest Alerts
              </WorkOrderBadge>
            )}
            <OccupancyBadge propertyId={workOrder.property?.id} reservationsByProperty={reservationsByProperty} />
            {workOrder.status !== 'OPEN' && (
              <WorkOrderBadge className={getStatusColor(workOrder.status)}>
                {workOrder.status.replace('_', ' ')}
              </WorkOrderBadge>
            )}
          </div>
        </div>

        {workOrder.commentCount > 0 && (
          <div className={`flex items-center p-2 border-l-4 border-t border-r border-b rounded-md mb-2 ${
            workOrder.urgentCommentCount > 0
              ? 'bg-orange-50 border-orange-500'
              : 'bg-blue-50 border-blue-500'
          }`}>
            <svg className={`h-5 w-5 mr-2 flex-shrink-0 ${
              workOrder.urgentCommentCount > 0 ? 'text-orange-700' : 'text-blue-700'
            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className={`text-sm font-medium ${
              workOrder.urgentCommentCount > 0 ? 'text-orange-800' : 'text-blue-800'
            }`}>
              {workOrder.urgentCommentCount > 0
                ? `${workOrder.urgentCommentCount} Urgent Comment${workOrder.urgentCommentCount > 1 ? 's' : ''}`
                : `${workOrder.commentCount} Comment${workOrder.commentCount > 1 ? 's' : ''}`
              }
            </span>
          </div>
        )}

        <div className="text-xs text-gray-500">
          Submitted: {format(new Date(workOrder.createdAt), 'MMM d, yyyy')}
          {workOrder.dueDate && (
            <span className="ml-2 text-blue-600">
              • Due: {formatDueDate(workOrder.dueDate)}
            </span>
          )}
        </div>

        {showAssignedBadge && (
          <div className="flex items-center py-1 px-2 bg-orange-50 rounded text-xs text-orange-800 w-fit">
            <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Assigned to you</span>
          </div>
        )}

        {showAssignmentDetails && !showAssignedBadge && (workOrder.assignedTeam || workOrder.assignedTo) && (
          <div className="flex items-center py-1 px-2 bg-blue-50 rounded text-xs text-blue-800 w-fit">
            <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Assigned to: {workOrder.assignedTeam?.name || workOrder.assignedTo?.name || 'Unknown'}</span>
          </div>
        )}

        {showAssignmentDetails && !workOrder.assignedTo && !workOrder.assignedTeam && (
          <div className="flex items-center py-1 px-2 bg-gray-50 rounded text-xs text-gray-600 w-fit">
            <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Unassigned</span>
          </div>
        )}

        <p className="text-sm text-gray-900 line-clamp-2">{workOrder.description}</p>

        <NextCheckInRow propertyId={workOrder.property?.id} reservationsByProperty={reservationsByProperty} />

        <div className="flex justify-between items-end pt-2">
          <div className="flex items-center">
            {workOrder.property?.area && (
              <AreaIndicator area={workOrder.property.area} />
            )}
          </div>
          <div className="flex space-x-2">
            <span
              className="inline-flex items-center px-3 py-1 border border-blue-600 text-blue-600 rounded-md text-sm"
              aria-hidden="true"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Details
            </span>
          </div>
        </div>
      </div>
    </li>
  );
};

export default WorkOrderListItem;
